import { Input } from '@casumo/dojo-react';
import classNames from 'classnames';
import React, { ChangeEvent, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useRegistrationStore } from '../../store/store.ts';
import { TIdentityProtectedPasswordField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { GTM_ERRORS } from '../../utils/gtm/constants.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';
import identityPasswordValidation from '../../utils/identityPasswordValidation.ts';

const IdentityProtectedPassword = ({
  name,
  value,
  label,
  instructionPartOne,
  instructionPartTwo,
  validation,
  onBlur,
  onKeyDown,
}: TIdentityProtectedPasswordField & {
  onBlur?: (field: Partial<TIdentityProtectedPasswordField>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
}) => {
  const {
    errors,
    setErrors,
    formData,
    setFormData,
    customValidations,
    setCustomValidations,
    metadata,
  } = useRegistrationStore();

  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const password: string = useMemo(() => value || formData?.[name] || '', [value, formData, name]);

  const handleBlur = async () => {
    setIsFocused(false);

    if (!identityPasswordValidation(password, formData)) {
      setCustomValidations({
        ...customValidations,
        [name]: 'Password should not contain your personal information',
      });

      GtmEvents.logAlert({
        alert: GTM_ERRORS.INVALID_PASSWORD,
        step:  metadata?.currentStep,
      });

      return;
    } else {
      setCustomValidations({
        ...customValidations,
        [name]: '',
      });
    }

    if (validation) {
      onBlur?.({ name, validation });
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrors({ ...errors, [name]: '' });
    setCustomValidations({ ...customValidations, [name]: '' });
    setFormData({ [name]: e.currentTarget.value });
    setCustomValidations({
      ...customValidations,
      [name]: '',
    });
  };

  useEffect(() => {
    if (!formData?.[name]) {
      setErrors({ ...errors, [name]: '' });
      setCustomValidations({ ...customValidations, [name]: '' });
    }
  }, [formData, name, formData?.[name]]);

  return (
    <div>
      <div onClick={
        !metadata?.isEmailValid
          ? () => {
              GtmEvents.logAlert({
                alert: GTM_ERRORS.DISABLED_PASSWORD_CLICK,
                step:  metadata?.currentStep,
              })
            }
          : undefined
      }>
        <Input
          id="identityProtectedPassword"
          ref={inputRef}
          type="password"
          name={name}
          label={label}
          value={password}
          error={errors[name] || customValidations[name] ? true : undefined}
          data-error={errors[name] || customValidations[name] ? true : undefined}
          disabled={!metadata?.isEmailValid}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          clearable
          onClear={() => setFormData({ [name]: '' })}
          data-testid={TEST_ID.INPUT + 'plainTextPassword'}
          onKeyDown={onKeyDown}
          className={classNames({ 'pointer-events-none': !metadata?.isEmailValid })}
        />
      </div>
      {(customValidations[name] || errors[name] || isFocused) && (
        <div className={classNames(
          'mt-2xs px-xs py-2xs rounded-sm bg-red-10 text-xs',
          {
            'bg-yellow-10': isFocused,
          })}>
          {instructionPartOne
            ?
              <div>
                {instructionPartOne}
              </div>
            : null
          }
          {instructionPartTwo
            ?
              <div className="text-red-40" dangerouslySetInnerHTML={{ __html: instructionPartTwo }} />
            : null
          }
        </div>
      )}
    </div>
  );
};

const Memoized = memo(IdentityProtectedPassword);
Memoized.displayName = 'IdentityProtectedPassword';

export default Memoized;
